import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import values from  'lodash/values';
import forEach from  'lodash/forEach';
import find from 'lodash/find';
import pick from 'lodash/pick';

import { SubmissionError } from 'redux-form';

import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';

// actions types
const FETCH_DATA_SUCCESS = 'landing-snippets/fetch-data-success';
const FETCH_DATA_ERROR = 'landing-snippets/fetch-data-error';

const SUBMIT_SUCCESS = 'landing-snippets/submit-success';
const SUBMIT_ERROR = 'landing-snippets/submit-error';

const REMOVE_SUCCESS = 'landing-snippets/remove-success';
const REMOVE_ERROR = 'landing-snippets/remove-error';

const SORT_BY_COLUMN = 'landing-snippets/table-sort-by-column';
const SHOW_MORE_ROWS = 'landing-snippets/table-show-more-rows';

const TOGGLE_PROGRESS_BAR = 'landing-snippets/toggle-progress-bar';

// permissions
export const allowedPermissions = {
	'view': 'landings_snippets_list',
	'create': 'landings_snippets_update',
	'edit': 'landings_snippets_update',
	'delete': 'landings_snippets_delete'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	table: {
		columns: {
			id: {
				index: 'id',
				title: 'Id',
				direction: 'desc'
			},
			name: {
				index: 'name',
				title: 'Name',
				direction: 'desc'
			},
			external_id: {
				index: 'external_id',
				title: 'External Id',
				direction: 'desc'
			},
			is_dangerous: {
				index: 'is_dangerous',
				title: 'Is dangerous'
			},
			created_at: {
				index: 'created_at',
				title: 'Created',
				direction: 'desc'
			},
			updated_at: {
				index: 'updated_at',
				title: 'Updated',
				direction: 'desc'
			},
			actions: {
				title: 'Actions'
			}
		},
		sortByColumn: {
			index: 'id',
			title: 'Id',
			direction: 'desc'
		},
		rowsNumber: 1000
	},
	form: {
		name: '',
		external_id: '',
		is_dangerous: false
	},
	error: false
};

// actions
export const actions = {
	fetchData: () => dispatch => {
		(http({
			url: `${endpoints.LANDING_SNIPPETS.BASE}`,
			method: 'get',
			actions: [
				FETCH_DATA_SUCCESS,
				FETCH_DATA_ERROR
			]
		}))(dispatch);
	},

	submitForm: (formData, id = null) => (dispatch, getState) => {
		const request = http({
			url: !id ? endpoints.LANDING_SNIPPETS.BASE : `${endpoints.LANDING_SNIPPETS.BASE}/${id}`,
			method: !id ? 'post' : 'put',
			actions: [
				SUBMIT_SUCCESS,
				SUBMIT_ERROR
			],
			data: pick({
				...formData,
				is_dangerous: !!formData.is_dangerous
			}, Object.keys(getState().landingSnippets.form))
		});

		return request(dispatch)
			.then(action => {
				if (action.type === SUBMIT_SUCCESS) {
					dispatch(actions.redirectToModuleIndex());
				}

				if (action.type === SUBMIT_ERROR && action.payload.response.status === 400) {
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					throw new SubmissionError(validationsErrors);
				}
			});
	},

	remove: id => dispatch => {
		const request = http({
			url: `${endpoints.LANDING_SNIPPETS.BASE}/${id}`,
			method: 'delete',
			actions: [
				REMOVE_SUCCESS,
				REMOVE_ERROR
			]
		});

		dispatch(actions.toggleProgressBar());

		request(dispatch);
	},

	sortByColumn: columnIndex => ({
		type: SORT_BY_COLUMN,
		payload: columnIndex
	}),

	showMoreRows: () => ({
		type: SHOW_MORE_ROWS
	}),

	redirectToModuleIndex: () => dispatch => {
		// @todo Adhock for redux-form submit functionality.
		setTimeout(() => dispatch(replace('landing-snippets/')), 100);
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	})};

// reducers
export default handleActions({
	[LOCATION_CHANGE]: (state, action) => {
		if (
			action.payload.pathname.indexOf('landing-snippets') !== -1 &&
			action.payload.pathname.indexOf('landing-snippets/create') === -1
		) {
			return {
				...state,
				data: null
			};
		}
		return state;
	},
	[FETCH_DATA_SUCCESS]: (state, action) => ({
		...state, data: action.payload.data, showProgressBar: false, error: false
	}),
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[SORT_BY_COLUMN]: (state, action) => {
		const { sortByColumn, columns }  = state.table;
		const columnIndex = action.payload;
		const direction = sortByColumn['index'] == columnIndex ?
			sortByColumn.direction === 'desc' ? 'asc' : 'desc' :
			columns[columnIndex].direction;
		return {
			...state,
			table: {
				...state.table,
				sortByColumn: {
					...columns[columnIndex],
					direction
				}
			}
		};
	},
	[SHOW_MORE_ROWS]: state => ({
		...state,
		table: {
			...state.table,
			rowsNumber: state.table.rowsNumber += initialState.table.rowsNumber
		}
	}),
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
	[REMOVE_SUCCESS]: state => ({...state, data: null}),
	[REMOVE_ERROR]: state => ({...state, error: true}),
	[SUBMIT_ERROR]: (state, action) => {
		const { response: {status = null, data = null} } = action.payload;
		if (status === 400 && !!data) return state;
		return { ...state, error: true };
	}
}, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.landingSnippets.data,
	getTable: state => {
		const { table, data } = state.landingSnippets;
		const rows = state.landingSnippets.data ? sortColumns(data, table.sortByColumn) : [];
		return {
			headers: values(table.columns),
			rows: rows.slice(0, table.rowsNumber),
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getIntl: state => intl(state),
	getProgressBar: state => state.landingSnippets.showProgressBar,
	getLandingSnippetsFieldsById: state => id => {
		const current = find(state.landingSnippets.data, { id: parseInt(id) });

		return {
			...state.landingSnippets.form,
			...current
		};
	},
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.landingSnippets.error
};

// functions
const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(rows, row => {
		const value = row[sortedColumn.index];
		switch (sortedColumn.index) {
			case 'id':
			case 'name':
			case 'external_id':
			case 'created_at':
			case 'updated_at':
				return value;
			default:
				return value;
		}
	}, sortedColumn.direction);
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/landing-snippets/index.js